import { notify, toast } from '@affine/component';
import { I18n } from '@affine/i18n';
import {
  type BlockPendingDeletionState,
  setBlockPendingDeletionSource,
} from '@blocksuite/affine/model';
import { LiveData, Service } from '@toeverything/infra';

import { OFFLINE_DELETE_COPY_CONFIRMATION } from '../../offline-mutation-outbox/services/offline-mutation-error';
import type { OfflineMutationOutboxService } from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import {
  OFFLINE_DELETION_HOLD_MS,
  type OfflineMutationRecord,
  type OfflineMutationStatus,
} from '../../offline-mutation-outbox/types';
import type { WorkspaceService } from '../../workspace';

/**
 * Tables and automations go to the server's trash, come back and leave
 * through the offline mutation outbox; the client never changes the host doc
 * before the receipt. This service is the one view of those queued requests:
 * the presentation per block, the actions of each phase, and the pending
 * deletion source page, canvas and sidebar read.
 */

export type BlockLifecycleOperation =
  | 'database.trash'
  | 'database.restore'
  | 'database.permanentDelete'
  | 'automation.trash'
  | 'automation.restore'
  | 'automation.permanentDelete';
export type BlockLifecycleRecord = {
  [Operation in BlockLifecycleOperation]: OfflineMutationRecord<Operation>;
}[BlockLifecycleOperation];
export type BlockLocalVisibility = 'hide' | 'keep';
type QueuedPresentation<State extends string> = {
  state: State;
  mutationId: string;
  operation: BlockLifecycleOperation;
  localVisibility: BlockLocalVisibility;
};
export type BlockDeletionPresentation =
  | { state: 'none' }
  | QueuedPresentation<'queued'>
  | QueuedPresentation<'confirming'>
  | QueuedPresentation<'unconfirmed'>;

/** Queued, sent and awaiting the receipt, or retries used up. */
const PRESENTATION_STATE = {
  pending: 'queued',
  flushing: 'confirming',
  failed: 'unconfirmed',
} as const satisfies Record<OfflineMutationStatus, string>;

const LIFECYCLE_OPERATIONS: ReadonlySet<string> =
  new Set<BlockLifecycleOperation>([
    'database.trash',
    'database.restore',
    'database.permanentDelete',
    'automation.trash',
    'automation.restore',
    'automation.permanentDelete',
  ]);
/** Only a queued trash shows on the block; a restore concerns an entry. */
const TRASH_OPERATIONS: ReadonlySet<BlockLifecycleOperation> =
  new Set<BlockLifecycleOperation>(['database.trash', 'automation.trash']);

const TRASH_QUEUED = 'com.streamlain.dataview.database.deletionPending.queued';
const TRASH_CANCEL = 'com.streamlain.dataview.database.deletionPending.cancel';
const TRASH_RUNNING = 'com.streamlain.trash.database-trash-running';

const NO_PRESENTATION: BlockDeletionPresentation = { state: 'none' };
const NONE_PENDING: BlockPendingDeletionState = { kind: 'none' };
const HIDDEN: BlockPendingDeletionState = { kind: 'hidden' };
const PENDING_PHASE: Record<
  Exclude<BlockDeletionPresentation['state'], 'none'>,
  BlockPendingDeletionState
> = {
  queued: { kind: 'visible', phase: 'queued' },
  confirming: { kind: 'visible', phase: 'confirming' },
  unconfirmed: { kind: 'visible', phase: 'unconfirmed' },
};

/** Presentations by doc and block id. */
type BlockPresentations = ReadonlyMap<
  string,
  ReadonlyMap<string, BlockDeletionPresentation>
>;

export function isBlockLifecycleMutation(
  mutation: OfflineMutationRecord
): mutation is BlockLifecycleRecord {
  return LIFECYCLE_OPERATIONS.has(mutation.operation);
}

export class BlockDeletionPresentationService extends Service {
  /** The presentation of every block with a queued or unconfirmed request. */
  readonly presentations$ = LiveData.computed(get =>
    blockPresentations(
      get(this.outbox.pendingMutations$),
      get(this.outbox.failedMutations$)
    )
  );

  /** Pending states by doc and block id, kept for allocation-free reads. */
  private pendingStates = new Map<
    string,
    Map<string, BlockPendingDeletionState>
  >();
  private readonly pendingListeners = new Set<() => void>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly outbox: OfflineMutationOutboxService
  ) {
    super();
    this.registerPendingDeletionSource();
  }

  presentation$(
    docId: string,
    blockId: string
  ): LiveData<BlockDeletionPresentation> {
    return this.presentations$.map(
      blocks => blocks.get(docId)?.get(blockId) ?? NO_PRESENTATION
    );
  }

  /** True when the request will never be sent; see the outbox. */
  cancelPending(mutationId: string): Promise<boolean> {
    return this.outbox.cancelPending(mutationId);
  }

  /** Sends an unconfirmed request again under its operation id. */
  retryUnconfirmed(mutationId: string): Promise<void> {
    return this.outbox.retryFailed(mutationId);
  }

  /**
   * Withdraws a queued trash. When it was sent already, the user reads that
   * it runs; nothing is promised.
   */
  async cancelQueuedTrash(mutationId: string): Promise<boolean> {
    const cancelled = await this.cancelPending(mutationId);
    if (!cancelled) toast(I18n.t(TRASH_RUNNING));
    return cancelled;
  }

  /** The note of a queued trash, with its cancel for the hold time. */
  announceQueued(mutationId: string): void {
    notify(
      {
        title: I18n.t(TRASH_QUEUED),
        actions: [
          {
            key: 'cancel',
            label: I18n.t(TRASH_CANCEL),
            autoClose: true,
            onClick: async () => {
              await this.cancelQueuedTrash(mutationId);
            },
          },
        ],
      },
      { duration: OFFLINE_DELETION_HOLD_MS }
    );
  }

  /**
   * The queued trash of this workspace's blocks, as page, canvas and sidebar
   * read it: a block nobody needs in place hides until the receipt, any other
   * stays with its phase; an unconfirmed one stays visible so it can be
   * checked again. A queued restore concerns an entry in the trash and shows
   * nothing.
   */
  private registerPendingDeletionSource(): void {
    const subscription = this.presentations$.subscribe(blocks => {
      this.pendingStates = pendingDeletionStates(blocks);
      for (const listener of this.pendingListeners) listener();
    });
    const workspace = this.workspaceService.workspace.docCollection;
    setBlockPendingDeletionSource(workspace, {
      state: (docId, blockId) =>
        this.pendingStates.get(docId)?.get(blockId) ?? NONE_PENDING,
      subscribe: onChange => {
        this.pendingListeners.add(onChange);
        return () => {
          this.pendingListeners.delete(onChange);
        };
      },
      cancel: async (docId, blockId) => {
        const presentation = this.presentations$.value.get(docId)?.get(blockId);
        if (presentation?.state !== 'queued') return false;
        return this.cancelQueuedTrash(presentation.mutationId);
      },
      retry: async (docId, blockId) => {
        const presentation = this.presentations$.value.get(docId)?.get(blockId);
        if (presentation?.state !== 'unconfirmed') return;
        await this.retryUnconfirmed(presentation.mutationId);
      },
    });
    this.disposables.push(() => {
      subscription.unsubscribe();
      this.pendingListeners.clear();
      setBlockPendingDeletionSource(workspace, null);
    });
  }
}

/** A table nobody reads may hide while queued; everything else stays. */
function localVisibilityOf(
  mutation: BlockLifecycleRecord
): BlockLocalVisibility {
  return mutation.operation === 'database.trash' ||
    mutation.operation === 'database.restore'
    ? mutation.payload.localVisibility
    : 'keep';
}

/**
 * Per block the latest queued request, else the latest one whose retries
 * ran out; refusals leave the outbox and show nothing.
 */
function blockPresentations(
  pending: readonly OfflineMutationRecord[],
  failed: readonly OfflineMutationRecord[]
): BlockPresentations {
  const docs = new Map<string, Map<string, BlockDeletionPresentation>>();
  const put = (mutation: BlockLifecycleRecord) => {
    const { docId, blockId } = mutation.payload;
    let blocks = docs.get(docId);
    if (!blocks) {
      blocks = new Map();
      docs.set(docId, blocks);
    }
    blocks.set(blockId, {
      state: PRESENTATION_STATE[mutation.status],
      mutationId: mutation.id,
      operation: mutation.operation,
      localVisibility: localVisibilityOf(mutation),
    });
  };
  // A later entry overwrites an earlier one: queued wins over unconfirmed.
  for (const mutation of failed) {
    if (
      isBlockLifecycleMutation(mutation) &&
      (mutation.failure === 'retries-exhausted' ||
        mutation.lastError === OFFLINE_DELETE_COPY_CONFIRMATION)
    ) {
      put(mutation);
    }
  }
  for (const mutation of pending) {
    if (isBlockLifecycleMutation(mutation)) put(mutation);
  }
  return docs;
}

function pendingDeletionStates(
  blocks: BlockPresentations
): Map<string, Map<string, BlockPendingDeletionState>> {
  const states = new Map<string, Map<string, BlockPendingDeletionState>>();
  for (const [docId, presentations] of blocks) {
    for (const [blockId, presentation] of presentations) {
      if (
        presentation.state === 'none' ||
        !TRASH_OPERATIONS.has(presentation.operation)
      ) {
        continue;
      }
      const state =
        presentation.state !== 'unconfirmed' &&
        presentation.localVisibility === 'hide'
          ? HIDDEN
          : PENDING_PHASE[presentation.state];
      let docStates = states.get(docId);
      if (!docStates) {
        docStates = new Map();
        states.set(docId, docStates);
      }
      docStates.set(blockId, state);
    }
  }
  return states;
}
